import {
  TAX_TYPE_TO_TRANSLATION_KEY,
  type TaxType,
  type TaxDetail,
  type AdditionalFee,
  type RoomData,
  type AggregatedData,
  type PaymentDisplayType,
  PAYMENT_POLICIES,
  PAYMENT_DISPLAY_TYPES,
} from './types'

// Helper function to find matching taxDetail for an additionalFee to extract the name
export const findMatchingTaxDetail = (
  fee: AdditionalFee,
  taxDetails: TaxDetail[] = []
): TaxDetail | undefined => {
  if (fee.name) {
    return undefined
  }

  return taxDetails.find(tax => {
    if (fee.local) {
      return fee.local.amount === tax.amount && fee.local.currency === tax.currency
    }

    return fee.amount === tax.amount && fee.currency === tax.currency
  })
}

// Helper function to convert AdditionalFee to TaxDetail
export const convertAdditionalFeeToTaxDetail = (
  fee: AdditionalFee,
  taxDetails: TaxDetail[] = []
): TaxDetail => {
  const matchingTax = findMatchingTaxDetail(fee, taxDetails)

  return {
    amount: fee.amount,
    currency: fee.currency,
    included: false,
    name: matchingTax?.name || fee.name || 'additional_fee',
    client: {
      amount: fee.amount,
      currency: fee.currency,
    },
    local: fee.local
      ? {
          amount: fee.local.amount,
          currency: fee.local.currency,
        }
      : undefined,
  }
}

// Helper function to get all taxes (taxDetails + additionalFees converted)
export const getAllTaxDetails = (
  taxDetails: TaxDetail[] = [],
  additionalFees: AdditionalFee[] = []
): TaxDetail[] => {
  const includedTaxDetails = taxDetails.filter(tax => tax.included === true)

  const notIncludedTaxDetails = additionalFees.map(fee =>
    convertAdditionalFeeToTaxDetail(fee, taxDetails)
  )

  return [...includedTaxDetails, ...notIncludedTaxDetails]
}

// Helper function to check if a tax name is a known tax type
export const isKnownTaxType = (taxName: string): taxName is TaxType => {
  return taxName in TAX_TYPE_TO_TRANSLATION_KEY
}

// Helper function to get translation key for a tax type
export const getTaxTranslationKey = (taxType: TaxType): string => {
  return TAX_TYPE_TO_TRANSLATION_KEY[taxType]
}

// Group tax details by name and included status, summing amounts
export const groupTaxDetails = (taxDetails: TaxDetail[]): TaxDetail[] => {
  const grouped = taxDetails.reduce(
    (acc, tax) => {
      const key = tax.name

      if (acc[key]) {
        acc[key].amount += tax.amount
        const existingClient = acc[key].client

        if (existingClient && tax.client) {
          existingClient.amount += tax.client.amount
        } else if (tax.client && !acc[key].client) {
          acc[key].client = { ...tax.client }
        }
      } else {
        acc[key] = { ...tax }
      }

      return acc
    },
    {} as Record<string, TaxDetail>
  )

  return Object.values(grouped)
}

// Calculate total tax amount, prioritizing client amount when available
export const getTotalTaxAmount = (taxDetails: TaxDetail[]): number => {
  return taxDetails.reduce((total, tax) => {
    const amount = tax.client?.amount ?? tax.amount
    return total + amount
  }, 0)
}

// Get totals for all taxes/fees by payment timing
export const getAllTotalsByPayment = (taxDetails: TaxDetail[]) => {
  const included = taxDetails.filter(item => item.included)
  const notIncluded = taxDetails.filter(item => !item.included)

  return {
    total: getTotalTaxAmount(taxDetails),
    payNow: getTotalTaxAmount(included),
    payLater: getTotalTaxAmount(notIncluded),
    currency: taxDetails[0]?.client?.currency ?? taxDetails[0]?.currency,
  }
}

// Extract currency from the first tax detail where included is false
export const getNotIncludedTaxCurrency = (taxDetails: TaxDetail[]): string | undefined => {
  const notIncludedTax = taxDetails.find(tax => tax.included === false)

  if (!notIncludedTax) {
    return undefined
  }

  return notIncludedTax.local?.currency ?? notIncludedTax.currency
}

// Helper function to determine payment display type
const determinePaymentDisplayType = (
  paymentPolicy: string | undefined,
  payDate: string | undefined,
  payNow: number
): PaymentDisplayType => {
  if (paymentPolicy === PAYMENT_POLICIES.NON_REFUNDABLE) {
    return PAYMENT_DISPLAY_TYPES.NON_REFUNDABLE
  }

  if (paymentPolicy === PAYMENT_POLICIES.NO_RESERVATION && payDate) {
    if (!payNow) {
      return PAYMENT_DISPLAY_TYPES.PAY_ON_ARRIVAL_ONLY
    }
    return PAYMENT_DISPLAY_TYPES.PAY_UPON_BOOKING_WITH_LATER_PAYMENT
  }

  if (paymentPolicy === PAYMENT_POLICIES.STAY_FOR_LONG && payDate) {
    return PAYMENT_DISPLAY_TYPES.STAY_FOR_LONG_DEPOSIT
  }

  return PAYMENT_DISPLAY_TYPES.NONE
}

// Calculate aggregated totals for all rooms including insurance (PDP now comes from totalData)
export const calculateAggregatedTotals = (rooms: RoomData[]): AggregatedData => {
  const aggregated = rooms.reduce<Omit<AggregatedData, 'paymentDisplayType'>>(
    (acc, room) => ({
      subtotal: acc.subtotal + room.subtotal,
      subtotalCurrency: room.subtotalCurrency || acc.subtotalCurrency,
      total: acc.total + room.total,
      totalCurrency: room.totalCurrency || acc.totalCurrency,
      totalWithFees: (acc.totalWithFees || 0) + (room.totalWithFees || room.total),
      payNow: (acc.payNow || 0) + (room.payNow || 0),
      payLate: (acc.payLate || 0) + (room.payLate || 0),
      roomsNumber: acc.roomsNumber + 1,
      isMultiRoom: false,
      taxDetails: [
        ...(acc.taxDetails || []),
        ...getAllTaxDetails(room.taxDetails, room.additionalFees),
      ],
      insurance: (acc.insurance || 0) + (room.insurance || 0),
      paymentPolicy: acc.paymentPolicy ?? room.paymentPolicy,
      payDate: acc.payDate ?? room.payDate,
      insuranceCurrency: room.insuranceCurrency || acc.insuranceCurrency,
      insuranceSelected: acc.insuranceSelected || room.insuranceSelected || false,
      hasInsurance: false,
      insuranceAmount: 0,
      totalWithInsurance: 0,
      payNowWithInsurance: 0,
    }),
    {
      subtotal: 0,
      subtotalCurrency: '',
      total: 0,
      totalCurrency: '',
      totalWithFees: 0,
      payNow: 0,
      payLate: 0,
      roomsNumber: 0,
      isMultiRoom: false,
      taxDetails: [] as TaxDetail[],
      insurance: 0,
      paymentPolicy: undefined as string | undefined,
      payDate: undefined as string | undefined,
      insuranceCurrency: undefined as string | undefined,
      insuranceSelected: false,
      hasInsurance: false,
      insuranceAmount: 0,
      totalWithInsurance: 0,
      payNowWithInsurance: 0,
    }
  )

  // Set isMultiRoom flag once we know how many rooms were aggregated
  const isMultiRoom = aggregated.roomsNumber > 1
  aggregated.isMultiRoom = isMultiRoom

  // Calculate payments by date for rooms with specific pay dates
  const paymentsByDate = new Map<string, number>()

  rooms.forEach(room => {
    if (room.payDate) {
      // For rooms with a payDate, calculate how much needs to be paid on that date
      const payNow = room.payNow || 0
      const total = room.total || 0
      const payOnDate = total - payNow // Amount to pay on the specific date

      if (payOnDate > 0) {
        const existingAmount = paymentsByDate.get(room.payDate) || 0
        paymentsByDate.set(room.payDate, existingAmount + payOnDate)
      }
    }
  })

  // Group tax details to avoid duplicates
  aggregated.taxDetails = groupTaxDetails(aggregated.taxDetails || [])

  // Calculate insurance details
  const hasInsurance = aggregated.insuranceSelected && (aggregated.insurance || 0) > 0
  const insuranceAmount = hasInsurance ? aggregated.insurance || 0 : 0
  const totalWithInsurance = (aggregated.totalWithFees || 0) + insuranceAmount
  const payNowWithInsurance = (aggregated.payNow || 0) + insuranceAmount

  // Determine payment display type
  const paymentDisplayType = isMultiRoom
    ? PAYMENT_DISPLAY_TYPES.NONE
    : determinePaymentDisplayType(
        aggregated.paymentPolicy,
        aggregated.payDate,
        aggregated.payNow || 0
      )

  return {
    ...aggregated,
    hasInsurance: hasInsurance || false,
    insuranceAmount,
    totalWithInsurance,
    payNowWithInsurance,
    paymentDisplayType,
    paymentsByDate: Array.from(paymentsByDate.entries()).map(([date, amount]) => ({
      date,
      amount,
      currency: aggregated.totalCurrency,
    })),
  }
}

export const getBattlefaceLabel = (key: string) => {
  const labelMap: Record<string, string> = {
    battleface_tripCancellation: 'insurance-package.essential',
    battleface_base: 'insurance-package.advanced',
    battleface_comprehensive: 'insurance-package.complete',
  }

  const baseKey = key.includes('_') ? key.slice(0, key.lastIndexOf('_')) : key
  return labelMap[baseKey] || key
}
